// FLAGS: silent-failure
// Runs once per document load, before React mounts: resolves the LanguagePathPrefix flag (fail-closed, bounded
// wait), initialises i18next only once that answer is known, then sets `<html lang>`. Consent is deliberately
// NOT here — the Cookiebot script stays first in `index.html`'s head, where its culture comes from the hostname
// map; while the prefix flag is off that yields the same culture this boot would compute, and OI-25358 owns
// making the banner follow a path prefix. The flag read is an axios call, so the boot registers the
// interceptors itself first (idempotent). `boot()` never rejects: a failure is reported and the app boots in
// legacy mode.
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { appInsights } from "@src/appInsights/AppInsights";
import { registerInterceptors } from "@src/AxiosSetup";
import {
  BOOT_TELEMETRY_PROPERTIES,
  reportBootFailure,
} from "@src/boot/bootLoader";
import i18n, { initI18n } from "@src/i18n";
import { getFeatureFlagValue } from "@src/services/featureFlagService";

export const BOOT_FLAG_TIMEOUT_MS = 1500;

export interface BootFlags {
  languagePathPrefix: boolean;
}

const bootFlags: BootFlags = { languagePathPrefix: false };

// FLAGS: silent-failure
// A failing flag endpoint boots the app in legacy mode, indistinguishable from the flag being off (the
// service already swallows errors). A slow one does the same but leaves one warning trace.
const resolveFlag = async (key: string): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      appInsights.trackTrace({
        message: `Boot flag ${key} timed out after ${BOOT_FLAG_TIMEOUT_MS} ms; booting in legacy mode`,
        severityLevel: SeverityLevel.Warning,
        properties: { ...BOOT_TELEMETRY_PROPERTIES, flag: key },
      });
      resolve(false);
    }, BOOT_FLAG_TIMEOUT_MS);
  });
  try {
    return await Promise.race([getFeatureFlagValue(key), timeout]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

// The decision every later reader must use; do not re-read the flag with `useFeatureFlag` for boot-level choices.
export const getBootFlags = (): Readonly<BootFlags> =>
  Object.freeze({ ...bootFlags });

const boot = async (): Promise<Readonly<BootFlags>> => {
  registerInterceptors();
  try {
    bootFlags.languagePathPrefix = await resolveFlag("LanguagePathPrefix");
    initI18n();
    document.documentElement.lang = i18n.language;
  } catch (error) {
    reportBootFailure(error);
  }
  return getBootFlags();
};

export default boot;
